import { isMobileOrTabletDevice } from 'customer-flows/utils/is-mobile-device'
import type { TrackingMode } from 'customer-flows/services/frame-manager'

const FLAG = 'close-confirmation-popup'

export const BOOKING_ABANDONMENT_PARTICIPATING_MODES: TrackingMode[] = ['standalone', 'modal', 'embedded']

export default function bookingAbandonment({ services, stores }: CommandArgs) {
  const posthog: PosthogTracker = services['trackers/posthog']
  const frameManager: FrameManager = services['frame-manager']
  const intentionalNavigation: IntentionalNavigation = services['intentional-navigation']
  const orderStore = stores['order'] as OrderStore

  function isParticipating(): boolean {
    if (isMobileOrTabletDevice()) return false

    return BOOKING_ABANDONMENT_PARTICIPATING_MODES.includes(frameManager.trackingMode)
  }

  /**
   * Sends the experiment exposure for visitors in a participating mode on a desktop device.
   */
  function trackExposure(): void {
    if (!isParticipating()) return

    posthog.isVariantFor(FLAG, 'test')
  }

  /**
   * Whether the visitor is in a participating mode on a desktop device and was assigned the test variant.
   * Reads the flag without sending an exposure.
   */
  function isActive(): boolean {
    if (!isParticipating()) return false

    return posthog.isVariantFor(FLAG, 'test', { sendEvent: false }) === true
  }

  /**
   * Whether the close button should ask for confirmation: modal mode only, and only before an order exists.
   */
  function shouldConfirmClose(): boolean {
    if (frameManager.trackingMode !== 'modal') return false
    if (orderStore.orderId) return false

    return isActive()
  }

  /**
   * Whether leaving the page right now should trigger the browser's native prompt:
   * only before an order exists, and never when the app is navigating away on purpose.
   */
  function shouldBlockUnload(): boolean {
    if (!isActive()) return false
    if (orderStore.orderId) return false

    return !intentionalNavigation.isLeaving
  }

  function leave() {
    frameManager.postMessage('close-iframe')
  }

  return {
    trackExposure,
    isActive,
    shouldConfirmClose,
    shouldBlockUnload,
    leave
  }
}

declare global {
  export type BookingAbandonmentCommands = ReturnType<typeof bookingAbandonment>

  interface CommandRegistry {
    'booking-abandonment': BookingAbandonmentCommands
  }
}
